<template>
  <div class="relative min-h-screen bg-gradient-to-br from-gray-100 to-blue-50">

    <!-- Toggle Filter Button (top-left corner) -->
    <button @click="toggleFilter"
      class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
      <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
    </button>

    <!-- Sliding Sidebar Filter -->
    <transition name="slide">
      <aside v-show="showFilter" class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r">
        <div class="p-6 border-b">
          <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
            <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
            Filters
          </h2>
        </div>
        <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Entities</label>
            <multiselect v-model="filters.entities" :options="entityOptions" :multiple="true" :close-on-select="false"
              placeholder="Select entities" track-by="id" label="name">
            </multiselect>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Campuses</label>
            <multiselect v-model="filters.campuses" :options="campusOptionsFiltered" :multiple="true"
              :close-on-select="false" placeholder="Select campuses" track-by="id" label="name">
            </multiselect>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">User Types</label>
            <multiselect v-model="filters.userTypes" :options="userTypeOptions" :multiple="true"
              :close-on-select="false" placeholder="Select user types" track-by="id" label="name">
            </multiselect>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Departments</label>
            <multiselect v-model="filters.departments" :options="departmentOptions" :multiple="true"
              :close-on-select="false" placeholder="Select departments" track-by="id" label="name">
            </multiselect>
          </div>
        </div>

        <!-- Apply Button -->
        <div class="p-6 flex justify-center space-x-6 border-t">
          <button @click.stop="clearFilters"
            class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold">
            Clear
          </button>
          <button @click.stop="applyFilters"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold">
            Apply
          </button>
        </div>
      </aside>
    </transition>

    <div class="h-full container mx-auto">
      <div class="flex flex-col h-full w-full p-0 sm:p-8">
        <!-- Breadcrumb -->
        <nav class="text-sm text-gray-600 mb-6 flex pt-6 sm:pt-0 pl-4 sm:pl-0">
          <router-link to="/dashboard" class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
          <span class="mx-1">/</span>
          <span class="text-blue-900 font-semibold">Innovation and Projects</span>
        </nav>

        <!-- Page Title Card -->
        <div class="bg-white sm:rounded-3xl shadow-md p-4 sm:p-8 mb-4 sm:mb-10 sm:border border-gray-200">
          <h1 class="text-xl sm:text-3xl font-bold text-blue-900 flex items-center gap-3">
            🚀 Innovation and Projects
          </h1>
          <p class="text-gray-500 mt-2 text-xs sm:text-sm">Share your innovative projects and research initiatives to
            showcase creativity and collaboration.</p>
        </div>

        <!-- Dashboard Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <!-- Total Innovations Card -->
          <div class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-blue-100 text-sm font-medium">Total Innovations</p>
                <p class="text-3xl font-bold mt-2">{{ formatNumber(innovationStatistics.totalInnovations)
                }}</p>
                <p class="text-blue-100 text-xs mt-1">Submitted Projects</p>
              </div>
              <div class="bg-blue-400 bg-opacity-30 rounded-full p-3">
                <font-awesome-icon :icon="['fas', 'lightbulb']" class="text-2xl" />
              </div>
            </div>
          </div>

          <!-- Top Entity Card -->
          <div class="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-purple-100 text-sm font-medium">Top Entity</p>
                <p class="text-2xl font-bold mt-2">{{ getTopEntity() }}</p>
                <p class="text-purple-100 text-xs mt-1">{{ getTopEntityCount() }} innovations</p>
              </div>
              <div class="bg-purple-400 bg-opacity-30 rounded-full p-3">
                <font-awesome-icon :icon="['fas', 'building']" class="text-2xl" />
              </div>
            </div>
          </div>

          <!-- Top Project Type Card -->
          <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-green-100 text-sm font-medium">Top Project Type</p>
                <p class="text-2xl font-bold mt-2">{{ getTopProjectType() }}</p>
                <p class="text-green-100 text-xs mt-1">{{ getTopProjectTypeCount() }} innovations</p>
              </div>
              <div class="bg-green-400 bg-opacity-30 rounded-full p-3">
                <font-awesome-icon :icon="['fas', 'folder']" class="text-2xl" />
              </div>
            </div>
          </div>

          <!-- Top User Type Card -->
          <div class="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-orange-100 text-sm font-medium">Top User Type</p>
                <p class="text-2xl font-bold mt-2">{{ getTopUserType() }}</p>
                <p class="text-orange-100 text-xs mt-1">{{ getTopUserTypeCount() }} innovations</p>
              </div>
              <div class="bg-orange-400 bg-opacity-30 rounded-full p-3">
                <font-awesome-icon :icon="['fas', 'users']" class="text-2xl" />
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Section -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <!-- Innovations by Entity Chart -->
          <InnovationsByEntityChart :data="innovationStatistics.innovationsByEntity"
            @entity-clicked="onEntityChartClick" />
          <!-- Innovations by Project Type Chart -->
          <InnovationsByProjectTypeChart :data="innovationStatistics.innovationsByProjectType"
            @project-type-clicked="onProjectTypeChartClick" />
          <!-- Innovations by User Type Chart -->
          <InnovationsByUserTypeChart :data="innovationStatistics.innovationsByUserType"
            @user-type-clicked="onUserTypeChartClick" />
        </div>

        <!-- Advanced Search (after charts) -->
        <div class="px-4 sm:px-0 mb-4">
          <InnovationAdvancedSearch :initial-basic-search="basicSearchText" :initial-advanced-search="advancedSearch"
            @on-basic-search="onBasicSearch" @on-advanced-search="onAdvancedSearch" @on-clear-search="clearSearch" />
        </div>

        <!-- Projects Section -->
        <section class="bg-white sm:rounded-3xl shadow-md p-0 sm:p-8 sm:border border-gray-200 flex-auto flex flex-col">
          <div class="flex justify-between items-center flex-wrap gap-4 sm:mb-6 p-4 sm:p-0 border-b-4 sm:border-b-0">
            <h2 class="text-lg sm:text-2xl font-semibold text-blue-900">🎯 Submitted Projects</h2>
            <button @click="showForm = true"
              class="flex items-center px-2 py-1 sm:px-5 sm:py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full shadow-lg hover:from-green-600 hover:to-green-700 transition text-xs sm:text-base">
              <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
              Add Project
            </button>
          </div>

          <div v-if="hasItems" class="flex flex-col">
            <div class="grid md:grid-cols-2 gap-0 sm:gap-6">
              <InnovationPostWithComment v-for="(item, index) in items" :key="index" :item="item"
                :paginator-info="paginatorInfo" :index="index" :current-date-time="currentDateTime"
                @view-item="viewItem" @toggle-like="toggleLike" @update-comments-count="updateCommentsCount" />
            </div>
            <div class="mt-4 px-4 sm:px-0 pb-4 sm:pb-0">
              <pagination :paginatorInfo="paginatorInfo" :setPage="setPage">
              </pagination>
            </div>
          </div>
          <div v-else class="flex-auto flex flex-col justify-center items-center py-12 px-6">
            <div class="text-center max-w-md">
              <div class="mb-6">
                <div class="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                  <font-awesome-icon :icon="['fas', 'lightbulb']" class="h-12 w-12 text-gray-400" />
                </div>
              </div>
              <h2 class="text-2xl font-semibold text-gray-800 mb-3">No Projects Found</h2>
              <p class="text-gray-500 mb-6 leading-relaxed">
                We couldn't find any projects matching your current search criteria.
                Try adjusting your filters or search terms to find what you're looking for.
              </p>
              <div class="flex flex-col sm:flex-row gap-3 justify-center">
                <button @click="clearFilters()"
                  class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 text-sm font-medium">
                  <font-awesome-icon :icon="['fas', 'filter']" class="h-4 w-4 mr-2" />
                  Clear All Filters
                </button>
                <button @click="clearSearch()"
                  class="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 text-sm font-medium">
                  <font-awesome-icon :icon="['fas', 'search']" class="h-4 w-4 mr-2" />
                  Clear Search
                </button>
              </div>
              <div class="mt-8 pt-6 border-t border-gray-200">
                <p class="text-sm text-gray-400">
                  <font-awesome-icon :icon="['fas', 'lightbulb']" class="h-4 w-4 mr-1" />
                  Tip: Use the advanced search to find projects by type, date, or text.
                </p>
              </div>
            </div>
          </div>
        </section>

        <transition name="fade">
          <base-modal v-if="showForm" title="Submit a New Innovation Project" @close="closeForm">
            <form @submit.prevent="submitProject" class="px-8 pt-3">
              <div class="overflow-y-auto space-y-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Project Title</label>
                  <input v-model="form.project_title" type="text"
                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                    placeholder="Enter your project title" required />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Project Type</label>
                  <select v-model="form.project_type_code"
                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none">
                    <option value="">Select Project Type</option>
                    <option v-for="projectType in filteredProjectTypes" :key="projectType.short_code"
                      :value="projectType.short_code">
                      {{ projectType.name }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Project Category</label>
                  <select v-model="form.project_type"
                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none">
                    <option value="">Select Category</option>
                    <option value="individual">Individual</option>
                    <option value="group">Group</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    <input type="checkbox" v-model="form.is_company_funded" class="mr-2" />
                    Company Funded
                  </label>
                </div>
                <div v-if="form.is_company_funded">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input v-model="form.company_name" type="text"
                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                    placeholder="Enter company name" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea v-model="form.description" rows="4"
                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                    placeholder="Describe your innovation project in detail..." required></textarea>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Attachments
                    (Optional)</label>
                  <div v-for="(attachment, index) in form.attachments" :key="index"
                    class="mb-3 p-3 border border-gray-300 rounded-lg">
                    <div class="flex items-center gap-3">
                      <input type="file" @change="handleFileChange($event, index)" class="flex-1 text-sm" />
                      <input type="text" v-model="attachment.label" placeholder="Attachment name (optional)"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      <button type="button" @click="removeAttachment(index)"
                        class="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm">
                        Remove
                      </button>
                    </div>
                  </div>
                  <button type="button" @click="addAttachment"
                    class="mt-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">
                    + Add Attachment
                  </button>
                </div>
              </div>

              <div class="flex justify-end gap-3 mt-6">
                <button type="button" @click="cancelSubmit"
                  class="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition">
                  Cancel
                </button>
                <button type="submit"
                  class="px-5 py-2.5 bg-blue-600 text-white rounded-full shadow hover:bg-blue-700 transition">
                  Submit Project
                </button>
              </div>
            </form>
          </base-modal>
        </transition>

      </div>
    </div>

    <transition name="fade" mode="out-in">
      <base-full-modal v-if="isViewItem" :title="`View Innovation`" height="" @close="closeModal">
        <innovation-view class="text-left" :item="selectedItem" @close="closeModal"></innovation-view>
      </base-full-modal>
    </transition>

    <div class="h-full flex justify-center items-center" v-if="isLoading">
      <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>

  </div>
</template>

<script>
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import InnovationView from "./view.vue";
import InnovationPostWithComment from "./components/InnovationPostWithComment.vue";
import InnovationsByEntityChart from "./charts/InnovationsByEntityChart.vue";
import InnovationsByProjectTypeChart from "./charts/InnovationsByProjectTypeChart.vue";
import InnovationsByUserTypeChart from "./charts/InnovationsByUserTypeChart.vue";
import { formatDateToServerFormat } from "../../helpers/utils";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import { preloadSounds } from "../../helpers/soundUtils";
import InnovationAdvancedSearch from './components/InnovationAdvancedSearch.vue';

const LIST_INNOVATIONS_QUERY = gql`
  query ListInnovations($first: Int!, $page: Int!, $entities: [String!], $campuses: [String!], $departments: [String!], $userTypes: [String!], $search: String, $project_type_code: String, $date_from: Date, $date_to: Date) {
    listInnovations(first: $first, page: $page, entities: $entities, campuses: $campuses, departments: $departments, userTypes: $userTypes, search: $search, project_type_code: $project_type_code, date_from: $date_from, date_to: $date_to) {
      error
      error_code
      message
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
      data {
        token
        user {
            token
            name
            email
            phone_number
            profile_picture_url
            entity
            campuses
        }
        project_title
        project_type_code
        description
        project_type
        is_company_funded
        company_name
        created_at
        attachments {
          file_path
          file_name
          label
        }
        likes_count
        liked_by_me
        comments_count
      }
    }
  }
`;

const CREATE_INNOVATION_MUTATION = gql`
  mutation CreateInnovation($input: InnovationInput!) {
    createInnovation(input: $input) {
      error
      error_code
      message
    }
  }
`;

const TOGGLE_LIKE_INNOVATION = gql`
  mutation ToggleLikeInnovation($innovation_token: String!) {
    toggleLikeInnovation(innovation_token: $innovation_token) {
        error
        error_code
        message
        data {
            id
            likes_count
            liked_by_me
        }
    }
  }
`;

const GET_INNOVATION_STATISTICS = gql`
  query GetInnovationStatistics($entities: [String!], $campuses: [String!], $departments: [String!], $userTypes: [String!]) {
    innovationStatistics(entities: $entities, campuses: $campuses, departments: $departments, userTypes: $userTypes) {
      error
      error_code
      message
      data {
        totalInnovations
        innovationsByEntity {
          entity
          count
        }
        innovationsByProjectType {
          project_type_code
          project_type_name
          count
        }
        innovationsByUserType {
          user_type
          user_type_name
          count
        }
      }
    }
  }
`;

const GET_INNOVATION_PROJECT_TYPES = gql`
  query GetInnovationProjectTypes($role_type: String) {
    innovationProjectTypesForRole(role_type: $role_type) {
      short_code
      name
      role_type
    }
  }
`;

const GET_INNOVATION_USER_TYPES = gql`
  query GetInnovationUserTypes {
    innovationUserTypes {
      error
      error_code
      message
      data {
        role
        role_to_display
      }
    }
  }
`;

const GET_INNOVATION_DEPARTMENTS = gql`
  query GetInnovationDepartments($roles: [String!]) {
    innovationUserDepartments(roles: $roles) {
      error
      error_code
      message
      data {
        department
      }
    }
  }
`;

export default {
  name: "innovation-index",
  components: {
    Pagination,
    InnovationView,
    InnovationPostWithComment,
    InnovationsByEntityChart,
    InnovationsByProjectTypeChart,
    InnovationsByUserTypeChart,
    Multiselect,
    InnovationAdvancedSearch,
  },
  data() {
    return {
      showFilter: false,
      basicSearchText: "",
      advancedSearch: {
        project_type_code: "",
        date_from: "",
        date_to: "",
      },
      filters: {
        entities: [],
        campuses: [],
        userTypes: [],
        departments: [],
      },
      entityOptions: [
        { id: 'FCHS', name: 'FCHS' },
        { id: 'ADPOLY', name: 'ADPOLY' },
        { id: 'ATHS', name: 'ATS' },
        { id: 'ADVETI', name: 'ADVETI' },
      ],
      campusOptions: [],
      projectTypes: [],
      userTypeOptions: [],
      departmentOptions: [],
      settings: [],
      currentDateTime: this.getFormatDateToServerFormat(new Date()),
      timeUpdater: null,
      first: 6,
      page: 1,
      isLoading: false,
      showForm: false,
      form: {
        project_title: "",
        project_type_code: "",
        description: "",
        attachments: [],
        project_type: "",
        is_company_funded: false,
        company_name: "",
      },
      isViewItem: false,
      selectedItem: null,
      innovationList: [],
      innovationStatistics: {
        totalInnovations: 0,
        innovationsByEntity: [],
        innovationsByProjectType: [],
        innovationsByUserType: [],
      },
    };
  },
  created() {
    this.loadData();
    this.fetchInnovationStatistics();
    preloadSounds();
    this.fetchCampuses();
    this.fetchProjectTypes();
    this.fetchUserTypes();
    this.fetchDepartments();
  },
  watch: {
    settings(val) {
      if (val) {
        this.$store.commit("settings/setSettings", val);

        let current = null;

        if (Array.isArray(val)) {
          const found = val.find(setting => setting.name === "current_datetime");
          if (found && found.value) {
            current = found.value;
          }
        }

        if (!current) {
          const now = new Date();
          current = this.getFormatDateToServerFormat(now);
        }

        this.currentDateTime = current;

        this.startCurrentDateTimeUpdater();
      }
    },
    'filters.entities': {
      handler() {
        // Reset campuses if they are not in the filtered list anymore
        const allowed = this.campusOptionsFiltered.map(c => c.id);
        this.filters.campuses = (this.filters.campuses || []).filter(c => allowed.indexOf(c.id) !== -1);
      },
      deep: true
    },
    'filters.userTypes': {
      handler() {
        // Refetch departments when user types change
        this.filters.departments = [];
        this.fetchDepartments();
      },
      deep: true
    },
  },
  computed: {
    role() {
      return this.$store.getters.role;
    },
    items() {
      return this.innovationList.data;
    },
    hasItems() {
      if (this.items && this.items.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    paginatorInfo() {
      return this.innovationList.paginatorInfo;
    },
    campusOptionsFiltered() {
      const selectedEntities = (this.filters.entities || []).map(e => e.id);
      const byEntity = selectedEntities.length
        ? this.campusOptions.filter(c => selectedEntities.indexOf(c.entity) !== -1)
        : this.campusOptions;
      return byEntity
        .filter(c => c.name && c.name !== '')
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    filteredProjectTypes() {
      // Filter project types based on user role
      // role_type is now an array, so we check if it includes the user's role
      const userRoleType = this.role === 'user' ? 'user' : 'faculty';
      return this.projectTypes.filter(pt => {
        // Check if role_type array includes the user's role type
        return Array.isArray(pt.role_type) && pt.role_type.includes(userRoleType);
      });
    },
  },
  apollo: {
    settings() {
      return {
        query: gql`query GetSettings {
                    settings {
                        name
                        value
                    }
                }`,
        fetchPolicy: 'cache-and-network'
      }
    },
  },
  methods: {
    toggleFilter() {
      this.showFilter = !this.showFilter;
    },
    async loadData() {
      this.isLoading = true;
      try {
        const response = await this.$apollo.query({
          query: LIST_INNOVATIONS_QUERY,
          variables: {
            first: this.first,
            page: this.page,
            entities: (this.filters.entities || []).map(e => e.id),
            campuses: (this.filters.campuses || []).map(c => c.id),
            departments: (this.filters.departments || []).map(d => d.id),
            userTypes: (this.filters.userTypes || []).map(u => u.id),
            search: this.basicSearchText || null,
            project_type_code: this.advancedSearch.project_type_code || null,
            date_from: this.advancedSearch.date_from || null,
            date_to: this.advancedSearch.date_to || null,
          },
          fetchPolicy: 'network-only'
        });

        this.innovationList = response.data.listInnovations;

      } catch (error) {
        console.error("Failed to load innovations:", error);
      }
      this.isLoading = false;
    },
    clearFilters() {
      this.showFilter = false;
      this.filters = {
        entities: [],
        campuses: [],
        userTypes: [],
        departments: [],
      };
      this.basicSearchText = "";
      this.advancedSearch = {
        project_type_code: "",
        budget_required: null,
        amount_min: null,
        amount_max: null,
        date_from: "",
        date_to: "",
      };
      this.loadData();
      this.fetchInnovationStatistics();
    },
    clearSearch() {
      this.basicSearchText = "";
      this.advancedSearch = {
        project_type_code: "",
        date_from: "",
        date_to: "",
      };
      this.setPage(1);
    },
    onBasicSearch(text) {
      this.basicSearchText = text;
      this.setPage(1);
    },
    onAdvancedSearch(filters) {
      this.advancedSearch = { ...this.advancedSearch, ...filters };
      this.setPage(1);
    },
    applyFilters() {
      this.toggleFilter();
      this.loadData();
      this.fetchInnovationStatistics();
    },
    setPage(page) {
      this.page = page;
      this.loadData();
    },
    addAttachment() {
      this.form.attachments.push({
        file: null,
        label: ''
      });
    },
    handleFileChange(event, index) {
      if (event.target.files && event.target.files[0]) {
        this.form.attachments[index].file = event.target.files[0];
      }
    },
    removeAttachment(index) {
      this.form.attachments.splice(index, 1);
    },
    async submitProject() {
      this.isLoading = true;

      try {
        // Prepare attachments and labels separately
        const attachments = this.form.attachments
          .filter(att => att.file)
          .map(att => att.file);

        const attachmentLabels = this.form.attachments
          .filter(att => att.file)
          .map(att => att.label || null);

        const { data } = await this.$apollo.mutate({
          mutation: CREATE_INNOVATION_MUTATION,
          variables: {
            input: {
              project_title: this.form.project_title,
              project_type_code: this.form.project_type_code,
              description: this.form.description,
              attachments: attachments.length > 0 ? attachments : null,
              attachmentLabels: attachmentLabels.length > 0 ? attachmentLabels : null,
              project_type: this.form.project_type || null,
              is_company_funded: this.form.is_company_funded || false,
              company_name: this.form.is_company_funded ? (this.form.company_name || null) : null,
            }
          },
        });

        const response = data.createInnovation;

        if (response.error) {
          return this.$swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: response.message,
          });
        }

        this.$swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: response.message,
          showConfirmButton: false,
          timer: 1000,
          timerProgressBar: true,
        });

        this.loadData();
        this.fetchInnovationStatistics();

      } catch (error) {
        this.$swal.fire({
          icon: 'error',
          title: 'Failed!',
          text: error.message || 'Could not create Innovation.',
        });
      } finally {
        this.isLoading = false;
      }

      this.resetForm();
      this.showForm = false;
    },
    cancelSubmit() {
      this.showForm = false;
      this.resetForm();
    },
    resetForm() {
      this.form = {
        project_title: "",
        project_type_code: "",
        description: "",
        attachments: [],
        project_type: "",
        is_company_funded: false,
        company_name: "",
      };
    },
    viewItem(item) {
      this.selectedItem = item;
      this.isViewItem = true;
    },
    closeModal() {
      this.isViewItem = false;
      this.selectedItem = null;
    },
    getFormatDateToServerFormat(date) {
      return formatDateToServerFormat(date);
    },
    startCurrentDateTimeUpdater() {
      if (this.timeUpdater) clearInterval(this.timeUpdater);

      this.timeUpdater = setInterval(() => {
        const updated = new Date(this.currentDateTime);
        updated.setMinutes(updated.getMinutes() + 1);
        this.currentDateTime = this.getFormatDateToServerFormat(updated);
      }, 60000);
    },
    async toggleLike(token, index) {

      const originalItem = this.innovationList.data[index];
      const optimisticLiked = !originalItem.liked_by_me;
      const optimisticCount = optimisticLiked
        ? originalItem.likes_count + 1
        : originalItem.likes_count - 1;

      const updatedData = [...this.innovationList.data];
      updatedData[index] = {
        ...originalItem,
        liked_by_me: optimisticLiked,
        likes_count: optimisticCount,
      };

      this.innovationList = {
        ...this.innovationList,
        data: updatedData,
      };

      try {
        const { data } = await this.$apollo.mutate({
          mutation: TOGGLE_LIKE_INNOVATION,
          variables: {
            innovation_token: token
          },
        });

        const updatedInnovation = data.toggleLikeInnovation.data;

        const updatedList = this.innovationList.data.map(item => {
          if (item.token === token) {
            return {
              ...item,
              liked_by_me: updatedInnovation.liked_by_me,
              likes_count: updatedInnovation.likes_count
            };
          }
          return item;
        });

        this.innovationList = {
          ...this.innovationList,
          data: updatedList
        };

      } catch (error) {
        this.$swal.fire({
          icon: 'error',
          title: 'Failed!',
          text: error.message || 'Could not like/unlike Innovation.',
        });
      }
    },
    updateCommentsCount(token) {

      const updatedList = this.innovationList.data.map(item => {
        if (item.token === token) {
          return {
            ...item,
            comments_count: (item.comments_count || 0) + 1
          };
        }
        return item;
      });

      this.innovationList = {
        ...this.innovationList,
        data: updatedList
      };

    },
    closeForm() {
      this.showForm = false;
      this.resetForm();
    },
    async fetchInnovationStatistics() {
      try {
        const response = await this.$apollo.query({
          query: GET_INNOVATION_STATISTICS,
          variables: {
            entities: (this.filters.entities || []).map(e => e.id),
            campuses: (this.filters.campuses || []).map(c => c.id),
            departments: (this.filters.departments || []).map(d => d.id),
            userTypes: (this.filters.userTypes || []).map(u => u.id),
          },
          fetchPolicy: "network-only",
        });

        if (response.data.innovationStatistics.error) {
          console.error("GraphQL Error:", response.data.innovationStatistics.message);
          return;
        }

        this.innovationStatistics = response.data.innovationStatistics.data;
      } catch (error) {
        console.error("Failed to load innovation statistics:", error);
      }
    },
    formatNumber(num) {
      return num.toLocaleString();
    },
    getTopEntity() {
      if (this.innovationStatistics.innovationsByEntity && this.innovationStatistics.innovationsByEntity.length > 0) {
        return this.innovationStatistics.innovationsByEntity[0].entity.replace(/ATHS/g, "ATS");
      }
      return "N/A";
    },
    getTopEntityCount() {
      if (this.innovationStatistics.innovationsByEntity && this.innovationStatistics.innovationsByEntity.length > 0) {
        return this.innovationStatistics.innovationsByEntity[0].count;
      }
      return 0;
    },
    getTopProjectType() {
      if (this.innovationStatistics.innovationsByProjectType && this.innovationStatistics.innovationsByProjectType.length > 0) {
        return this.innovationStatistics.innovationsByProjectType[0].project_type_name || 'N/A';
      }
      return "N/A";
    },
    getTopProjectTypeCount() {
      if (this.innovationStatistics.innovationsByProjectType && this.innovationStatistics.innovationsByProjectType.length > 0) {
        return this.innovationStatistics.innovationsByProjectType[0].count;
      }
      return 0;
    },
    getTopUserType() {
      if (this.innovationStatistics.innovationsByUserType && this.innovationStatistics.innovationsByUserType.length > 0) {
        return this.innovationStatistics.innovationsByUserType[0].user_type_name || 'N/A';
      }
      return "N/A";
    },
    getTopUserTypeCount() {
      if (this.innovationStatistics.innovationsByUserType && this.innovationStatistics.innovationsByUserType.length > 0) {
        return this.innovationStatistics.innovationsByUserType[0].count;
      }
      return 0;
    },
    onEntityChartClick(entity) {
      this.filters.entities = [{ id: entity, name: entity.replace(/ATHS/g, 'ATS') }];
      this.loadData();
      this.fetchInnovationStatistics();
    },
    onProjectTypeChartClick(projectTypeCode) {
      // Find the project type in the options
      const projectType = this.projectTypes.find(pt => pt.short_code === projectTypeCode);
      if (projectType) {
        this.advancedSearch.project_type_code = projectTypeCode;
        this.onAdvancedSearch(this.advancedSearch);
      }
    },
    onUserTypeChartClick(userType) {
      // Find the user type in the options
      const userTypeOption = this.userTypeOptions.find(ut => ut.id === userType);
      if (userTypeOption) {
        this.filters.userTypes = [userTypeOption];
        this.applyFilters();
      }
    },
    async fetchCampuses() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`query GetCampusesPublic {
            campusesPublic {
              error
              message
              data {
                Entity
                portal_campus
              }
            }
          }`,
          fetchPolicy: 'network-only'
        });

        if (data && data.campusesPublic && data.campusesPublic.data) {
          const rows = data.campusesPublic.data;
          this.campusOptions = rows
            .filter(r => r && r.portal_campus)
            .map(r => ({ id: r.portal_campus, name: r.portal_campus, entity: (r.Entity || '').trim() }));
        }
      } catch (error) {
        console.error('Failed to fetch campuses:', error);
      }
    },
    async fetchProjectTypes() {
      try {
        // Determine role type: 'user' if role is 'user', otherwise 'faculty'
        const roleType = this.role === 'user' ? 'user' : 'faculty';

        const response = await this.$apollo.query({
          query: GET_INNOVATION_PROJECT_TYPES,
          variables: {
            role_type: roleType
          },
          fetchPolicy: 'network-only'
        });

        if (response.data && response.data.innovationProjectTypesForRole) {
          this.projectTypes = response.data.innovationProjectTypesForRole;
        }
      } catch (error) {
        console.error('Failed to fetch project types:', error);
      }
    },
    async fetchUserTypes() {
      try {
        const { data } = await this.$apollo.query({
          query: GET_INNOVATION_USER_TYPES,
          fetchPolicy: 'network-only'
        });

        if (data && data.innovationUserTypes && data.innovationUserTypes.data) {
          this.userTypeOptions = data.innovationUserTypes.data.map(userType => ({
            id: userType.role,
            name: userType.role_to_display
          }));
        }
      } catch (error) {
        console.error('Failed to fetch user types:', error);
      }
    },
    async fetchDepartments() {
      try {
        const selectedRoles = (this.filters.userTypes || []).map(u => u.id);
        const { data } = await this.$apollo.query({
          query: GET_INNOVATION_DEPARTMENTS,
          variables: {
            roles: selectedRoles.length > 0 ? selectedRoles : null,
          },
          fetchPolicy: 'network-only'
        });

        if (data && data.innovationUserDepartments && data.innovationUserDepartments.data) {
          this.departmentOptions = data.innovationUserDepartments.data.map(dept => ({
            id: dept.department,
            name: dept.department
          }));
        }
      } catch (error) {
        console.error('Failed to fetch departments:', error);
      }
    },
  },

  beforeUnmount() {
    if (this.timeUpdater) clearInterval(this.timeUpdater);
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

.ht-18 {
  height: 18px;
}
</style>
